import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldAlert,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  ExternalLink,
  User,
  Briefcase,
  Scale,
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import { conflictChecksApi } from "../../services/api";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface SearchTerm {
  name?: string;
  email?: string;
  phone?: string;
}

interface ConflictMatch {
  _id: string;
  score: number;
  matchType: string;
  matchedName: string;
  searchTerm: string;
  clientId?: string;
  clientName?: string;
  matterId?: string;
  matterTitle?: string;
  matterNumber?: string;
  relationship?: string;
  description?: string;
}

interface ConflictCheckResult {
  _id: string;
  status: string;
  matches: ConflictMatch[];
  recordsScanned?: number;
}

interface ConflictCheckModalProps {
  open: boolean;
  onClose: () => void;
  searchTerms: SearchTerm[];
  excludeClientId?: string;
  onResolved?: (status: string) => void;
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

function scoreColor(score: number) {
  if (score >= 80) return { bg: "bg-red-100", text: "text-red-700" };
  if (score >= 50) return { bg: "bg-amber-100", text: "text-amber-700" };
  return { bg: "bg-slate-100", text: "text-slate-600" };
}

function matchTypeBadge(type: string) {
  switch (type) {
    case "former_client":
      return { label: "Former Client", className: "bg-red-100 text-red-700" };
    case "opposing_party":
      return { label: "Opposing Party", className: "bg-amber-100 text-amber-700" };
    case "opposing_counsel":
      return { label: "Opposing Counsel", className: "bg-orange-100 text-orange-700" };
    case "related_party":
      return { label: "Related Party", className: "bg-purple-100 text-purple-700" };
    case "current_client":
      return { label: "Current Client", className: "bg-blue-100 text-blue-700" };
    default:
      return { label: type?.replace(/_/g, " ") || "Unknown", className: "bg-slate-100 text-slate-600" };
  }
}

/* ─── Subtle Particles ───────────────────────────────────────────────── */

const ClearParticles: React.FC = () => {
  const particles = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    x: Math.cos((i / 8) * Math.PI * 2) * 40,
    y: Math.sin((i / 8) * Math.PI * 2) * 40,
    delay: i * 0.06,
    size: 2 + Math.random() * 3,
  }));

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            backgroundColor: p.id % 3 === 0 ? "#10B981" : p.id % 3 === 1 ? "#34D399" : "#6EE7B7",
          }}
          initial={{ opacity: 0, x: 0, y: 0, scale: 0 }}
          animate={{
            opacity: [0, 1, 0],
            x: p.x,
            y: p.y,
            scale: [0, 1.2, 0],
          }}
          transition={{
            duration: 0.8,
            delay: 0.3 + p.delay,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
};

/* ─── Component ──────────────────────────────────────────────────────── */

const ConflictCheckModal: React.FC<ConflictCheckModalProps> = ({
  open,
  onClose,
  searchTerms,
  excludeClientId,
  onResolved,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ConflictCheckResult | null>(null);
  const [resolving, setResolving] = useState(false);
  const [waiverMode, setWaiverMode] = useState(false);
  const [waiverReason, setWaiverReason] = useState("");
  const hasRun = useRef(false);

  // Auto-run search on mount when open
  useEffect(() => {
    if (!open || hasRun.current || searchTerms.length === 0) return;
    hasRun.current = true;

    const runSearch = async () => {
      setLoading(true);
      try {
        const res = await conflictChecksApi.search({
          searchTerms,
          excludeClientId,
        });
        setResult(res.data);
      } catch (err) {
        console.error("Conflict check failed:", err);
      } finally {
        setLoading(false);
      }
    };
    runSearch();
  }, [open, searchTerms, excludeClientId]);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      hasRun.current = false;
      setResult(null);
      setWaiverMode(false);
      setWaiverReason("");
    }
  }, [open]);

  const handleResolve = async (status: string) => {
    if (!result?._id) return;
    setResolving(true);
    try {
      const data: any = { status };
      if (status === "waived") data.waiverReason = waiverReason;
      await conflictChecksApi.resolve(result._id, data);
      onResolved?.(status);
    } catch (err) {
      console.error("Failed to resolve conflict check:", err);
    } finally {
      setResolving(false);
    }
  };

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="Conflict of Interest Check"
      subtitle="Searching for potential conflicts..."
      icon={Scale}
      size="lg"
    >
      <div className="min-h-[200px]">
        {/* Loading */}
        {loading && (
          <motion.div
            className="flex flex-col items-center justify-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
            >
              <ShieldAlert className="h-10 w-10 text-emerald-600 mb-4" />
            </motion.div>
            <p className="text-base font-semibold text-slate-700 mb-1">Scanning records...</p>
            <p className="text-sm text-slate-500 text-center">
              Checking clients, matters, opposing parties, and related contacts.
            </p>
          </motion.div>
        )}

        {/* Clear result */}
        {!loading && result && (result.status === "clear" || (result.matches && result.matches.length === 0)) && (
          <motion.div
            className="py-8 text-center relative"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <ClearParticles />
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.1 }}
            >
              <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4 relative z-10">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
            </motion.div>
            <h3 className="text-xl font-bold text-emerald-800 mb-1 relative z-10">No Conflicts Found</h3>
            <p className="text-sm text-slate-500 relative z-10">
              {result.recordsScanned
                ? `Scanned ${result.recordsScanned.toLocaleString()} records.`
                : "All records have been checked."}
            </p>
            <p className="text-xs text-slate-400 mt-3 relative z-10">
              This check has been recorded for your compliance records.
            </p>

            <button
              onClick={onClose}
              className="mt-6 px-6 py-2.5 rounded-xl text-white text-sm font-bold transition-colors relative z-10"
              style={{ backgroundColor: "#0F5132" }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#146C43"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#0F5132"}
            >
              Continue
            </button>
          </motion.div>
        )}

        {/* Potential conflicts */}
        {!loading && result && result.matches && result.matches.length > 0 && (
          <div className="space-y-4">
            <motion.div
              className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-200"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-amber-800">
                  {result.matches.length} Potential Conflict{result.matches.length !== 1 ? "s" : ""} Found
                </p>
                <p className="text-xs text-amber-700">Review each match and take appropriate action.</p>
              </div>
            </motion.div>

            {result.matches.map((match, idx) => {
              const sc = scoreColor(match.score);
              const mtb = matchTypeBadge(match.matchType);
              return (
                <motion.div
                  key={match._id || idx}
                  className="p-4 bg-slate-50 rounded-xl border border-slate-100"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + idx * 0.08 }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <motion.span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${sc.bg} ${sc.text}`}
                      initial={{ scale: 0.5 }}
                      animate={{ scale: [0.5, 1.15, 1] }}
                      transition={{ duration: 0.4, delay: 0.2 + idx * 0.08 }}
                    >
                      {match.score}% match
                    </motion.span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${mtb.className}`}>
                      {mtb.label}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 mb-1">
                    <span className="font-medium">{match.matchedName}</span>
                  </p>
                  {match.matterTitle && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Briefcase className="h-3 w-3" />
                      {match.matterNumber && <span className="font-medium">{match.matterNumber}</span>}
                      {match.matterNumber && " — "}
                      {match.matterTitle}
                    </div>
                  )}
                  {match.clientName && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                      <User className="h-3 w-3" />
                      {match.clientName}
                    </div>
                  )}
                  {match.relationship && (
                    <p className="text-xs text-slate-500 italic mt-1">{match.relationship}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    {match.matterId && (
                      <button
                        onClick={() => { onClose(); navigate(`/matters/${match.matterId}`); }}
                        className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-100 transition-colors"
                      >
                        <ExternalLink className="h-2.5 w-2.5" />
                        View Matter
                      </button>
                    )}
                    {match.clientId && (
                      <button
                        onClick={() => { onClose(); navigate(`/clients/${match.clientId}`); }}
                        className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-100 transition-colors"
                      >
                        <ExternalLink className="h-2.5 w-2.5" />
                        View Client
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}

            {/* Resolution */}
            {result.status !== "clear" && result.status !== "confirmed" && result.status !== "waived" && (
              <div className="pt-2 border-t border-slate-200">
                {!waiverMode ? (
                  <div className="flex flex-wrap gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleResolve("clear")}
                      disabled={resolving}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
                      style={{ backgroundColor: "#198754" }}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Approve — No Conflict
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleResolve("confirmed")}
                      disabled={resolving}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      <XCircle className="h-4 w-4" />
                      Block
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setWaiverMode(true)}
                      disabled={resolving}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold bg-amber-600 hover:bg-amber-700 disabled:opacity-50 transition-colors"
                    >
                      <ShieldAlert className="h-4 w-4" />
                      Waive with Reason
                    </motion.button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <textarea
                      value={waiverReason}
                      onChange={(e) => setWaiverReason(e.target.value)}
                      rows={3}
                      placeholder="Explain why this conflict is being waived..."
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors resize-none"
                      autoFocus
                    />
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleResolve("waived")}
                        disabled={!waiverReason.trim() || resolving}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold bg-amber-600 hover:bg-amber-700 disabled:opacity-50 transition-colors"
                      >
                        {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                        Confirm Waiver
                      </button>
                      <button
                        onClick={() => { setWaiverMode(false); setWaiverReason(""); }}
                        className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </PremiumModal>
  );
};

export default ConflictCheckModal;
